// frontend/src/components/account/UserAddress.jsx
import React, { useEffect, useRef, useState } from 'react';
import { getMyProfile } from '../../services/userService';
import {
  getAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
  reverseGeocode,
  lookupPincode
} from '../../services/addressService';
import { ThreeDot } from 'react-loading-indicators';
import { useToast } from '../../contexts/ToastContext';
import "../../css/userDashboard.css";
// All 28 states and 8 union territories (the old list had only 9)
const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
  'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
  'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

// Map whatever the map/post-office service returns onto our state list
const matchState = (name) => {
  if (!name) return '';
  const n = String(name).toLowerCase().replace(/&/g, 'and').replace(/[^a-z]/g, '');
  const aliases = { nctofdelhi: 'Delhi', newdelhi: 'Delhi', pondicherry: 'Puducherry', orissa: 'Odisha' };
  if (aliases[n]) return aliases[n];
  return INDIAN_STATES.find((st) => st.toLowerCase().replace(/[^a-z]/g, '') === n) || '';
};

const LOCATION_ERRORS = {
  1: 'Location access is blocked. Allow location for this site in your browser settings, or enter the address below.',
  2: 'We could not get your location. Check that location (GPS) is on, or enter the address below.',
  3: 'Getting your location took too long. Try again, or enter the address below.'
};

const SUGGESTED_PLACES = [
  'Home', 'Office', 'Hostel', 'PG', 'Parents Home'
];

const UserAddress = () => {
  const { addToast, confirm } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addresses, setAddresses] = useState([]);

  const [addressId, setAddressId] = useState(null); // if not null → update mode
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    altPhone: '',
    address: '',
    pincode: '',
    landmark: '',
    city: '',
    locality: '',
    state: '',
    suggestedName: '',
    isDefault: false,
    latitude: null,
    longitude: null
  });

  // "Use my current location"
  const [locating, setLocating] = useState(false);
  const [locateMsg, setLocateMsg] = useState(null); // { type: 'ok' | 'error', text }
  const [localitySuggestions, setLocalitySuggestions] = useState([]);
  const addressInputRef = useRef(null);
  const autoFilled = useRef({ city: '', state: '' }); // values we filled, safe to replace

  const [showForm, setShowForm] = useState(false);

  // Load profile + existing addresses
  useEffect(() => {
    const load = async () => {
      try {
        const [profile, addrs] = await Promise.all([
          getMyProfile(),
          getAddresses()
        ]);

        setAddresses(addrs || []);
      } catch (err) {
        console.error('Failed to load', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ---------- Pincode -> fill city & state, suggest localities ----------
  const handlePincodeChange = async (value) => {
    const pin = value.replace(/\D/g, '').slice(0, 6);
    handleChange('pincode', pin);
    if (pin.length !== 6) {
      setLocalitySuggestions([]);
      return;
    }
    try {
      const info = await lookupPincode(pin);
      setLocalitySuggestions(info.localities || []);
      setForm((prev) => {
        const next = { ...prev };
        // only replace city/state if empty or previously auto-filled
        if (!prev.city || prev.city === autoFilled.current.city) next.city = info.city || prev.city;
        const st = matchState(info.state);
        if (st && (!prev.state || prev.state === autoFilled.current.state)) next.state = st;
        autoFilled.current = { city: next.city, state: next.state };
        return next;
      });
    } catch (err) {
      setLocalitySuggestions([]);
    }
  };

  // ---------- Use my current location ----------
  const handleUseLocation = () => {
    setLocateMsg(null);
    if (!('geolocation' in navigator)) {
      setLocateMsg({ type: 'error', text: 'Your browser cannot share location. Please enter the address below.' });
      return;
    }
    if (!window.isSecureContext) {
      setLocateMsg({ type: 'error', text: 'Location works only on a secure (https) site. Please enter the address below.' });
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const place = await reverseGeocode(coords.latitude, coords.longitude);
          const street = [place.house, place.street, place.area].filter(Boolean).join(', ');
          const state = matchState(place.state);
          setForm((prev) => ({
            ...prev,
            // keep anything the customer already typed in the address line
            address: prev.address || street,
            locality: place.locality || prev.locality,
            city: place.city || prev.city,
            pincode: place.pincode || prev.pincode,
            state: state || prev.state,
            latitude: Number(coords.latitude.toFixed(6)),
            longitude: Number(coords.longitude.toFixed(6))
          }));
          autoFilled.current = { city: place.city || '', state };
          if (place.pincode) handlePincodeSuggestions(place.pincode);

          const missing = [
            !place.house && 'house / flat number',
            !place.pincode && 'pincode'
          ].filter(Boolean);
          const approx = coords.accuracy > 150 ? ` Your location is approximate (about ${Math.round(coords.accuracy)} m).` : '';
          setLocateMsg({
            type: 'ok',
            text: `We filled in your area from your location.${approx} Please ${
              missing.length ? `add your ${missing.join(' and ')} and ` : ''
            }check the details before saving.`
          });
          setTimeout(() => addressInputRef.current?.focus(), 50);
        } catch (err) {
          setLocateMsg({
            type: 'error',
            text: err.response?.data?.message || 'Could not look up your address. Please enter it below.'
          });
        } finally {
          setLocating(false);
        }
      },
      (error) => {
        setLocating(false);
        setLocateMsg({ type: 'error', text: LOCATION_ERRORS[error.code] || LOCATION_ERRORS[2] });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  // locality suggestions for a pincode found via location (no form changes)
  const handlePincodeSuggestions = async (pin) => {
    try {
      const info = await lookupPincode(pin);
      setLocalitySuggestions(info.localities || []);
    } catch (err) {
      setLocalitySuggestions([]);
    }
  };

  const resetForm = () => {
    setForm({
      fullName: '',
      phone: '',
      altPhone: '',
      address: '',
      pincode: '',
      landmark: '',
      city: '',
      locality: '',
      state: '',
      suggestedName: '',
      isDefault: false,
      latitude: null,
      longitude: null
    });
    setAddressId(null);
    setLocateMsg(null);
    setLocalitySuggestions([]);
    autoFilled.current = { city: '', state: '' };
  };

  const handleEdit = (addr) => {
    setForm({
      fullName: addr.fullName || '',
      phone: addr.phone || '',
      altPhone: addr.altPhone || '',
      address: addr.address || '',
      pincode: addr.pincode || '',
      landmark: addr.landmark || '',
      city: addr.city || '',
      locality: addr.locality || '',
      state: addr.state || '',
      suggestedName: addr.suggestedName || '',
      isDefault: addr.isDefault ?? false,
      latitude: addr.latitude ?? null,
      longitude: addr.longitude ?? null
    });
    setAddressId(addr._id);
    setLocateMsg(null);
    setShowForm(true);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let updated;
      if (addressId) {
        updated = await updateAddress(addressId, form);
      } else {
        updated = await createAddress(form);
      }
      setAddresses(updated || []);
      window.dispatchEvent(new CustomEvent('addresses:changed', { detail: updated || [] }));
      setShowForm(false);
      resetForm();
      addToast(addressId ? 'Address updated' : 'Address saved');
    } catch (err) {
      console.error('Failed to save address', err);
      addToast(err.response?.data?.message || 'Could not save the address. Please check the details and try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete this address?',
      message: 'This address will be removed from your account.',
      confirmLabel: 'Delete'
    });
    if (!ok) return;

    try {
      const updated = await deleteAddress(id);
      setAddresses(updated || []);
      window.dispatchEvent(new CustomEvent('addresses:changed', { detail: updated || [] }));
      addToast('Address deleted successfully');
    } catch (err) {
      console.error('Delete failed', err);
      addToast('Failed to delete address', 'error');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      const updated = await setDefaultAddress(id);
      setAddresses(updated || []);
      window.dispatchEvent(new CustomEvent('addresses:changed', { detail: updated || [] }));
      addToast('Default address updated');
    } catch (err) {
      console.error('Set default failed', err);
      addToast('Failed to set default address', 'error');
    }
  };

  if (loading) {
    return <div className="user-section-loading"><center><ThreeDot color={["#32cd32", "#327fcd", "#cd32cd", "#cd8032"]} /></center></div>;
  }

  return (
    <div className="user-address-wrapper">
      {/* LIST OF ADDRESSES */}
      <div className="address-list-section">
        <h3 style={{ marginBottom: '1rem' }}>My Addresses</h3>

        {addresses.length === 0 ? (
          <p style={{ color: '#888', marginBottom: '1rem' }}>No addresses yet.</p>
        ) : (
          <div className="address-list">
            {addresses.map((addr) => (
              <div key={addr._id} className="address-item">
                <div className="address-item-header">
                  <strong>{addr.fullName}</strong>
                  {addr.suggestedName && (
                    <span className="address-badge">{addr.suggestedName}</span>
                  )}
                  {addr.isDefault && (
                    <span className="address-default-badge">Default</span>
                  )}
                </div>

                <div className="address-item-body">
                  <p className="address-line">{addr.address}</p>
                  {addr.locality && <p className="address-line">{addr.locality}</p>}
                  <p className="address-line">{addr.city} - {addr.pincode}</p>
                  <p className="address-line">{addr.state}</p>
                  <p className="address-line">Phone: {addr.phone}</p>
                </div>

                <div className="address-item-actions">
                  <button
                    className="btn-link"
                    onClick={() => handleEdit(addr)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-link"
                    onClick={() => handleDelete(addr._id)}
                  >
                    Delete
                  </button>
                  {!addr.isDefault && (
                    <button
                      className="btn-link"
                      onClick={() => handleSetDefault(addr._id)}
                    >
                      Set as Default
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!showForm && (
          <button
            className="btn-add-address"
            onClick={handleAddNew}
          >
            + Add New Address
          </button>
        )}
      </div>

      {/* ADD/EDIT FORM */}
      {showForm && (
        <div className="address-form-section">
          <h3>{addressId ? 'Edit Address' : 'Add New Address'}</h3>

          <div className="m-locate">
            <button
              type="button"
              className="m-locate-btn"
              onClick={handleUseLocation}
              disabled={locating}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3.2" />
                <circle cx="12" cy="12" r="7.5" />
                <path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3" />
              </svg>
              {locating ? 'Finding your location…' : 'Use my current location'}
            </button>
            {locateMsg && (
              <p className={`m-locate-msg ${locateMsg.type === 'error' ? 'is-error' : 'is-ok'}`} role="status">
                {locateMsg.text}
              </p>
            )}
            <div className="m-locate-or"><span>or enter the address manually</span></div>
          </div>

          <form onSubmit={handleSubmit} className="user-form address-form">
            {/* Full Name */}
            <div className="user-form-row">
              <label>
                Full Name<span className="required">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter Your Name"
                value={form.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                required
              />
            </div>

            {/* Mobile Number */}
            <div className="user-form-row">
              <label>
                Mobile number<span className="required">*</span>
              </label>
              <div className="user-phone-wrapper">
                <div className="user-phone-flag">+91</div>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Alternate Number */}
            <div className="user-form-row">
              <label>Alternate number (Optional)</label>
              <div className="user-phone-wrapper">
                <div className="user-phone-flag">+91</div>
                <input
                  type="tel"
                  placeholder="Alternate Number"
                  value={form.altPhone}
                  onChange={(e) => handleChange('altPhone', e.target.value)}
                />
              </div>
            </div>

            {/* Full address */}
            <div className="user-form-row">
              <label>
                House / flat no., building, street<span className="required">*</span>
              </label>
              <input
                ref={addressInputRef}
                type="text"
                placeholder="e.g. 12B, Lotus Apartments, North Street"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                required
              />
            </div>

            {/* Pincode + Landmark */}
            <div className="user-form-row address-two-col">
              <div>
                <label>
                  Pincode<span className="required">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="6-digit pincode"
                  value={form.pincode}
                  onChange={(e) => handlePincodeChange(e.target.value)}
                  pattern="[1-9][0-9]{5}"
                  title="6-digit pincode"
                  required
                />
              </div>
              <div>
                <label>Landmark (Optional)</label>
                <input
                  type="text"
                  placeholder="Landmark"
                  value={form.landmark}
                  onChange={(e) => handleChange('landmark', e.target.value)}
                />
              </div>
            </div>

            {/* City + Locality */}
            <div className="user-form-row address-two-col">
              <div>
                <label>
                  City<span className="required">*</span>
                </label>
                <input
                  type="text"
                  placeholder="City"
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  required
                />
              </div>
              <div>
                <label>Locality</label>
                <input
                  type="text"
                  placeholder="Locality / area"
                  list="locality-suggestions"
                  value={form.locality}
                  onChange={(e) => handleChange('locality', e.target.value)}
                />
                <datalist id="locality-suggestions">
                  {localitySuggestions.map((l) => (
                    <option key={l} value={l} />
                  ))}
                </datalist>
              </div>
            </div>

            {/* State dropdown */}
            <div className="user-form-row">
              <label>
                State<span className="required">*</span>
              </label>
              <div className="select-wrapper">
                <select
                  value={form.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  required
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map((st) => (
                    <option key={st} value={st}>
                      {st}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Address Type / Suggested name dropdown */}
            <div className="user-form-row">
              <label>Address Type (Optional)</label>
              <div className="select-wrapper">
                <select
                  value={form.suggestedName}
                  onChange={(e) =>
                    handleChange('suggestedName', e.target.value)
                  }
                >
                  <option value="">Select Type</option>
                  {SUGGESTED_PLACES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Make default */}
            <div className="user-form-row user-checkbox-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={form.isDefault}
                  onChange={(e) =>
                    handleChange('isDefault', e.target.checked)
                  }
                />
                <span>Set as Default Address</span>
              </label>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                className="user-save-btn"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              
            </div>
            <button
                type="button"
                className="user-cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default UserAddress;
